import React, { ChangeEvent, FocusEvent } from 'react';
import { Control, useController, UseControllerProps } from 'react-hook-form';
import MuiTextField, { TextFieldProps as MuiTextFieldProps } from '@mui/material/TextField';
import { memoizeField } from './memoizeField';
import FieldWrapper, { FormMethods } from './FieldWrapper';

type Props = {
  id?: string;
  name: string;
  control?: Control;
  controllerProps?: Omit<UseControllerProps, 'name'>;
  errorText?: React.ReactNode | string;
} & FormMethods & MuiTextFieldProps

function TextField(props: Props) {
  const {
    id,
    name,
    control,
    formMethods,
    fullWidth = true,
    controllerProps,
    errorText,
    onChange = () => {},
    onBlur = () => {},
    ...rest
  } = props;

  const { field, fieldState: { error } } = useController({
    name,
    control: formMethods?.control || control,
    rules: controllerProps?.rules,
    defaultValue: controllerProps?.defaultValue,
    shouldUnregister: controllerProps?.shouldUnregister,
  });

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onChange(event);
    field.onChange(event);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onBlur(event);

    if (field.onBlur) {
      field.onBlur();
    }
  };

  const shouldRenderError = error && errorText;

  return (
    <MuiTextField
      id={id}
      name={field.name}
      value={field.value}
      onBlur={handleBlur}
      onChange={handleChange}
      fullWidth={fullWidth}
      helperText={shouldRenderError ? errorText : ''}
      {...rest}
      ref={field.ref}
    />
  );
}

export default FieldWrapper(memoizeField(TextField));
